import React, { useState } from "react";
import { Input } from "antd";

/**
 * MoneyInput — numeric input for money/quantity fields.
 * Replaces the old "invalidChars" alphabet blocklists.
 * - accepts digits, '.', ',' (auto-normalized to '.') and "'" (Swiss grouping, stripped)
 * - stores the raw normalized value (e.g. "1234.55") via onChange({target:{name,value}})
 * - displays Swiss-formatted value (1'234.55) when not focused
 */
const sanitize = (raw) => {
  let v = String(raw ?? "").replace(/'/g, "").replace(",", ".");
  v = v.replace(/[^0-9.-]/g, "");
  // minus only allowed at the start
  v = v.replace(/(?!^)-/g, "");
  // keep only the first decimal separator
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  // max two decimals
  const [int, dec] = v.split(".");
  if (dec !== undefined) {
    v = `${int}.${dec.slice(0, 2)}`;
  }
  return v;
};

const formatSwiss = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  const [int, dec] = String(value).split(".");
  const sign = int.startsWith("-") ? "-" : "";
  const digits = sign ? int.slice(1) : int;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${sign}${grouped}${dec !== undefined ? `.${dec}` : ""}`;
};

const MoneyInput = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  className,
  style,
  plain = false,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    const sanitized = sanitize(e.target.value);
    onChange({ target: { name, value: sanitized } });
  };

  const handleBlur = (e) => {
    setFocused(false);
    if (onBlur) onBlur(e);
  };

  const displayValue = focused ? value ?? "" : formatSwiss(value);

  if (plain) {
    return (
      <input
        name={name}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        style={style}
        {...rest}
      />
    );
  }

  return (
    <Input
      name={name}
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default MoneyInput;
